import { useState, useEffect, useRef, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { TRFerSidebar } from './TRFerSidebar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { HelpFAB } from './HelpFAB';
import { RouteErrorBoundary } from '@/components/ErrorBoundary';
import { BottomNav } from './BottomNav';
import { LegalStrip } from './LegalStrip';
import { BottomSheetProvider } from './BottomSheetContext';
import { HeaderTitleProvider } from './HeaderTitleContext';
import { useAuth } from '@/contexts/AuthContext';
import { OnboardingGuideProvider } from '@/contexts/OnboardingGuideContext';
import { OnboardingGuideChip } from '@/components/onboarding/OnboardingGuideChip';
import { WelcomeBanner } from '@/components/onboarding/WelcomeBanner';
import { ProfileDialog } from '@/components/profile/ProfileDialog';
import { StevensWidgetProvider } from '@/components/stevens-widget/StevensWidgetContext';
import { StevensWidget } from '@/components/stevens-widget/StevensWidget';
import { GlobalVoicePip } from '@/features/chat-trf/voice/GlobalVoicePip';
import { SpotlightOverlay } from '@/components/stevens-widget/SpotlightOverlay';
import { SkipToContent } from '@/components/a11y/SkipToContent';
import { OfflineBanner } from '@/components/a11y/OfflineBanner';
import { LanguageSwitcher } from './LanguageSwitcher';
import { MachineTranslationBanner } from './MachineTranslationBanner';
import {
  getSidebarMode,
  setSidebarModeStorage,
  getSidebarSide,
  setSidebarSideStorage,
  type SidebarMode,
  type SidebarSide,
} from '@/components/settings/settings-storage';
import { SIDEBAR_WIDTH, applySidebarWidth, clampWidth, readPanelWidth, writePanelWidth } from '@/lib/layout-prefs';
import { isImmersiveRoute } from './immersive-routes';
import { useNavigationOrigin } from '@/lib/navigation-origin';
import { cn } from '@/lib/utils';

const LG_BREAKPOINT = '(min-width: 1024px)';
// Tailwind `md` — at/above this the sidebar is a pinned desktop rail; below it
// the sidebar is an overlay drawer (the "mobile/small viewport" experience).
const MD_BREAKPOINT = '(min-width: 768px)';

/** The content-area spinner while a lazy route's chunk loads. */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24" role="status" aria-busy="true">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

export function MainLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const immersive = isImmersiveRoute(pathname);
  // Wave w1024: every history entry remembers the in-app page it came from
  // (the header BackLink returns there, filters and all), each list path
  // remembers its last query (the fallback back link carries it), and
  // <main> — the shell's scroll container — gets its scroll back on browser
  // Back/Forward and on a back-link return. `lib/navigation-origin.ts`.
  const mainRef = useRef<HTMLElement>(null);
  useNavigationOrigin(mainRef);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(getSidebarMode);
  const [sidebarSide, setSidebarSide] = useState<SidebarSide>(getSidebarSide);
  // null = the user has never toggled: in `auto` mode the rail follows the
  // viewport until they do (#1705 item 2 — the toggle is offered in every
  // mode now, not only the `collapsed` setting).
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean | null>(() => {
    const raw = localStorage.getItem('trf_sidebar_expanded');
    return raw === null ? null : raw !== 'false';
  });
  const [isLgScreen, setIsLgScreen] = useState(
    () => window.matchMedia(LG_BREAKPOINT).matches
  );
  const [isMdScreen, setIsMdScreen] = useState(
    () => window.matchMedia(MD_BREAKPOINT).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(LG_BREAKPOINT);
    const handler = (e: MediaQueryListEvent) => setIsLgScreen(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(MD_BREAKPOINT);
    const handler = (e: MediaQueryListEvent) => setIsMdScreen(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Persist sidebar expanded state across sessions (only once the user has
  // expressed one — null means "follow the viewport").
  useEffect(() => {
    if (sidebarExpanded === null) return;
    localStorage.setItem('trf_sidebar_expanded', String(sidebarExpanded));
  }, [sidebarExpanded]);

  // #1705 item 7: the sidebar's width is the user's, per account, applied as
  // a CSS variable the rail and the content offset both read.
  const userId = user?.id ?? user?._id ?? '';
  const [sidebarWidth, setSidebarWidth] = useState<number>(
    () => readPanelWidth(userId, 'sidebar') ?? SIDEBAR_WIDTH.fallback,
  );
  useEffect(() => {
    const stored = readPanelWidth(userId, 'sidebar');
    setSidebarWidth(stored ?? SIDEBAR_WIDTH.fallback);
    applySidebarWidth(stored);
    return () => applySidebarWidth(null);
  }, [userId]);
  const onSidebarWidthChange = (px: number) => {
    const next = clampWidth(px, SIDEBAR_WIDTH);
    setSidebarWidth(next);
    writePanelWidth(userId, 'sidebar', next);
    applySidebarWidth(next);
  };

  // Listen for sidebar mode changes from the Settings page
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent<SidebarMode>).detail;
      setSidebarModeStorage(mode);
      setSidebarMode(mode);
      setSidebarExpanded(true);
    };
    window.addEventListener('trf:sidebarModeChanged', handler);
    return () => window.removeEventListener('trf:sidebarModeChanged', handler);
  }, []);

  // Listen for sidebar side (left/right) changes from the Settings page
  useEffect(() => {
    const handler = (e: Event) => {
      const next = (e as CustomEvent<SidebarSide>).detail;
      setSidebarSideStorage(next);
      setSidebarSide(next);
    };
    window.addEventListener('trf:sidebarSideChanged', handler);
    return () => window.removeEventListener('trf:sidebarSideChanged', handler);
  }, []);

  // Compute derived sidebar state from mode. `auto` follows the viewport
  // until the user toggles; `collapsed` honors the toggle (default expanded);
  // `hamburger` is never a pinned rail.
  const collapsedByMode =
    sidebarMode === 'auto'
      ? (sidebarExpanded === null ? !isLgScreen : !sidebarExpanded)
      : sidebarMode === 'collapsed'
      ? !(sidebarExpanded ?? true)
      : false;

  // When open as overlay drawer, always show full labels regardless of mode
  const collapsed = sidebarOpen ? false : collapsedByMode;

  // An immersive route overrides the stored mode for this render only (#1831);
  // `sidebarMode` itself is never rewritten, so leaving /chat hands the user
  // back exactly the rail they had.
  const pinned = !immersive && sidebarMode !== 'hamburger';

  // The collapse toggle is offered whenever the sidebar is a pinned rail
  // (#1705 item 2). It used to appear only under the `collapsed` setting, so
  // a parent on the default `auto` mode had no way to thin the sidebar. It
  // lives in the sidebar's brand block as the panel icon (2026-09-20; it was
  // the header hamburger, #1725).
  const onSidebarToggle = pinned ? () => setSidebarExpanded(collapsed) : undefined;

  // The profile row lives in the drawer; opening the profile closes the
  // drawer, or closing the dialog left the parent in a drawer they had
  // finished with (#2435).
  const openProfile = () => {
    setSidebarOpen(false);
    setProfileOpen(true);
  };

  // Applicants (pre-admission family-application flow) have no in-app
  // destinations — they only reach /apply/wizard + /apply/status — so the
  // sidebar is dead space. Render the app shell without it for them.
  const hideSidebar = user?.role === 'applicant';

  // The roles that get the persistent companion/Williams widget, whose
  // collapsed launcher floats over the bottom-right of every page.
  const hasWidgetLauncher = user?.role === 'parent' || user?.role === 'admin' || user?.role === 'trfer';

  // The side-flip lives in Sidebar.tsx (parent + admin). TRFer/Helper render
  // their own sidebar components which stay left-anchored, so force 'left' for
  // those roles to keep the content offset and drawer on the same edge.
  //
  // The preference is a mobile reachability aid: only honor it below `md`,
  // where the sidebar is an overlay drawer. At md+ it's a pinned desktop rail
  // and stays on the left so desktop layout is unchanged.
  const usesFlippableSidebar = user?.role === 'parent' || user?.role === 'admin';
  const effectiveSide: SidebarSide =
    usesFlippableSidebar && !isMdScreen ? sidebarSide : 'left';

  // Content offset mirrors sidebar width AND side when pinned (none when no
  // sidebar). A right-anchored sidebar pads the right edge instead of the left.
  // The parent/admin Sidebar reads the width variable; TRFerSidebar stays a
  // fixed 16rem rail, so its offset stays the fixed class.
  const wideOffset = user?.role === 'trfer'
    ? { left: 'md:pl-64', right: 'md:pr-64' }
    : { left: 'md:pl-[var(--trf-sidebar-w,16rem)]', right: 'md:pr-[var(--trf-sidebar-w,16rem)]' };
  const contentOffset = hideSidebar || !pinned
    ? ''
    : effectiveSide === 'right'
    ? collapsed
      ? 'md:pr-16'
      : wideOffset.right
    : collapsed
    ? 'md:pl-16'
    : wideOffset.left;

  return (
    <BottomSheetProvider>
    <HeaderTitleProvider>
    <StevensWidgetProvider>
    <OnboardingGuideProvider>
    <div className="h-full flex flex-col bg-stevens-bg text-stevens-ink overflow-hidden pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      <SkipToContent />
      <OfflineBanner />
      {hideSidebar ? null : user?.role === 'trfer' ? (
        <TRFerSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={collapsed}
          pinned={pinned}
          onToggle={onSidebarToggle}
          onProfileClick={openProfile}
        />
      ) : (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={collapsed}
          pinned={pinned}
          side={effectiveSide}
          width={sidebarWidth}
          onWidthChange={onSidebarWidthChange}
          onToggle={onSidebarToggle}
          onProfileClick={openProfile}
        />
      )}

      <div className={`flex-1 min-h-0 flex flex-col transition-[padding] duration-300 ${contentOffset}`}>
        {/* #2937: the impersonation banner is this column's first row, in the
            flow. As a fixed overlay at top-16 it covered the header, the
            consent strip and the page eyebrow, and the rail clipped its name.
            Here it pushes everything below it down and sits beside the rail. */}
        <ImpersonationBanner />
        {/* #2109 R1 (L4): the honesty banner for a machine-translated locale
            sits above the header so it is the first thing on every signed-in
            page, and the compact switcher rides in the same slim row so every
            page carries one (research §2.2 rule 8). In English the row is
            just the switcher, right-aligned, one line tall. */}
        <div className="flex items-center justify-end gap-3 px-4 pt-1 md:px-6 lg:px-8">
          <MachineTranslationBanner className="min-w-0 flex-1 rounded-md border" />
          <LanguageSwitcher />
        </div>
        <Header
          onMenuClick={() => setSidebarOpen((prev) => !prev)}
          onProfileClick={openProfile}
          // On an immersive route the rail is gone at every viewport, so the
          // drawer trigger is the only way back to the app nav (#1831).
          alwaysShowMenuButton={immersive || sidebarMode === 'hamburger'}
          hideMenuButton={hideSidebar}
        />

        <main
          ref={mainRef}
          id="main-content"
          // Top padding one step under the sides (#1725): with the 3rem header
          // the greeting no longer starts a full header-and-a-half down.
          // The LegalStrip is a row under <main> now (#2629), not a fixed
          // overlay, so the bottom padding no longer has to clear it.
          //
          // `relative` makes <main> the containing block for every absolutely
          // positioned descendant without a positioned ancestor of its own —
          // above all the `sr-only` inputs (the document rows' file pickers).
          // Without it their containing block was the viewport, <main>'s
          // overflow never clipped them, and a row below the fold stretched
          // the DOCUMENT: the window scrolled under this scroller and the
          // header slid off the top (#2278, 26 px at 500 px wide).
          className={cn(
            'relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-4 pt-3 md:px-6 md:pb-6 md:pt-4 lg:px-8 lg:pb-8 lg:pt-5',
            // #2278: the widget launcher (the Shield FAB, ChatPanelShell) is
            // fixed 52 px tall, 20 px off the bottom-right corner. The page's
            // last row must be able to scroll clear of it, or a right-aligned
            // button there (the committee card's Save) can never be pressed
            // from under it: 96 px = 72 px of launcher + a 24 px gap.
            !immersive && hasWidgetLauncher && 'pb-24 md:pb-24 lg:pb-24',
            // #2405: on the chat surface <main> is a flex column, so the
            // welcome banner takes its own height and the page slot below
            // fills the rest. The chat page's root is `h-full`; as a direct
            // child of a block <main> it was 100% PLUS the banner, which
            // pushed the composer under the BottomNav at 390 px.
            immersive && 'flex flex-col',
          )}
        >
          <WelcomeBanner />
          {/* The page slot: a flex-1 box on the chat surface (#2405); every
              other route gets `contents`, which drops the box so a routed
              page still sizes against <main> exactly as before. */}
          <div className={immersive ? 'flex-1 min-h-0 flex flex-col' : 'contents'}>
            <RouteErrorBoundary>
              {/* A lazy route suspends the content area only. The boundary at the
                  top of App.tsx sits above this layout, so without this one the
                  first visit to a lazy route (`/chat/:id`) blanked the sidebar
                  and header too. */}
              <Suspense fallback={<RouteFallback />}>
                <Outlet />
              </Suspense>
            </RouteErrorBoundary>
          </div>
        </main>

        {/* Legal links, bottom-centre of the content column (#768). A row
            under <main>, not a fixed overlay (#2629): fixed, it covered page
            controls at first paint. Not on an immersive route (#1831), where
            it would sit under the composer; the links stay reachable from
            every other page and from Settings. md+ only (the strip hides
            itself below md, where the BottomNav is). */}
        {immersive ? null : <LegalStrip />}

        {/* M2.12 / B-039 — BottomNav is now a flex-shrink-0 sibling of
            <main> instead of a position:fixed overlay. The Android WebView
            painted area is smaller than window.innerHeight on Capacitor;
            position:fixed bottom:0 landed below the visible viewport on
            routes whose body fit in one screen (e.g. /calendar). Making
            the layout a fixed app shell with an inner-scrolling <main>
            anchors the nav to the painted bottom edge instead. */}
        {user?.role === 'parent' && (
          <BottomNav side={effectiveSide} onMore={() => setSidebarOpen(true)} />
        )}
      </div>

      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
      />

      <OnboardingGuideChip />
      <HelpFAB sidebarPinned={pinned && !hideSidebar} />

      {/* Spec 10-1 / 11-1: Persistent companion/Williams widget */}
      {hasWidgetLauncher && (
        <>
          <StevensWidget />
          {/* #1731 (F-15): the app-level voice PiP, one session for every route. */}
          <GlobalVoicePip />
          <SpotlightOverlay />
        </>
      )}
    </div>
    </OnboardingGuideProvider>
    </StevensWidgetProvider>
    </HeaderTitleProvider>
    </BottomSheetProvider>
  );
}
